import { useAuthorizedRequest } from '@/hooks/useRequest';
import { apiRequest } from '@/lib/api';
import type { Client } from '@/lib/Client';
import type { Payment, PaymentAttributes, PaymentBy } from '@/lib/Payment';
import { t } from '@/lib/i18n';
import { Box, Button, Card, CloseButton, Drawer, Heading, HStack, Image, Input, Portal, Text, VStack } from '@chakra-ui/react';
import { useAuth0 } from '@auth0/auth0-react';
import { useCallback, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { mutate as globalMutate } from 'swr';

function jsonFetcher(url: string, token: string) {
    return fetch(url, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.json());
}

function formatDate(s: string): string {
    if (!s) return '—';
    return new Date(s).toLocaleString('uk-UA');
}

const selectStyle = {
    width: '100%', padding: '4px 8px', borderRadius: '4px',
    border: '1px solid #ccc', fontSize: '14px', background: 'transparent',
};

function EditPaymentDrawer({ payment, open, onClose, onSave }: {
    payment: Payment; open: boolean; onClose: () => void; onSave: (attrs: PaymentAttributes) => Promise<void>;
}) {
    const [amount, setAmount] = useState(String(payment.amount));
    const [dateTime, setDateTime] = useState(payment.date_time ? new Date(payment.date_time).toISOString().slice(0, 16) : '');
    const [comment, setComment] = useState(payment.comment ?? '');
    const [paymentBy, setPaymentBy] = useState<PaymentBy>(payment.payment_by);
    const [deleted, setDeleted] = useState(payment.deleted);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');

    async function handleSubmit() {
        const amt = parseInt(amount, 10);
        if (isNaN(amt) || amt < 0) { setError(t('payments.amount_invalid')); return; }
        setSubmitting(true); setError('');
        try {
            await onSave({ client_id: payment.client_id, amount: amt, date_time: new Date(dateTime).toISOString(), comment: comment.trim() || undefined, payment_by: paymentBy, deleted });
            onClose();
        } catch (e) { setError(e instanceof Error ? e.message : t('error.unknown')); }
        finally { setSubmitting(false); }
    }

    return (
        <Drawer.Root open={open} onOpenChange={e => { if (!e.open) onClose(); }} placement="end">
            <Portal><Drawer.Backdrop /><Drawer.Positioner><Drawer.Content>
                <Drawer.Header><Drawer.Title>{t('payment_detail.edit')}</Drawer.Title></Drawer.Header>
                <Drawer.Body><VStack gap={3} align="stretch">
                    {error && <Text color="red.500" fontSize="sm">{error}</Text>}
                    <Box><Text fontSize="sm" mb="1">{t('label.amount')} (₴) *</Text><Input size="sm" type="number" min="0" value={amount} onChange={e => setAmount(e.target.value)} /></Box>
                    <Box><Text fontSize="sm" mb="1">{t('label.date')} *</Text><Input size="sm" type="datetime-local" value={dateTime} onChange={e => setDateTime(e.target.value)} /></Box>
                    <Box><Text fontSize="sm" mb="1">{t('label.payment_by')} *</Text>
                        <select value={paymentBy} onChange={e => setPaymentBy(e.target.value as PaymentBy)} style={selectStyle}>
                            <option value="cash">{t('payment_by.cash')}</option><option value="card">{t('payment_by.card')}</option>
                        </select>
                    </Box>
                    <Box><Text fontSize="sm" mb="1">{t('label.comment')}</Text><Input size="sm" value={comment} onChange={e => setComment(e.target.value)} /></Box>
                    <label style={{ display: 'flex', gap: '8px', fontSize: '14px' }}><input type="checkbox" checked={deleted} onChange={e => setDeleted(e.target.checked)} /> {t('label.deleted')}</label>
                </VStack></Drawer.Body>
                <Drawer.Footer><HStack><Button size="sm" onClick={handleSubmit} loading={submitting}>{t('action.save')}</Button><Button size="sm" variant="ghost" onClick={onClose}>{t('action.cancel')}</Button></HStack></Drawer.Footer>
                <Drawer.CloseTrigger asChild><CloseButton size="xs" variant="solid" /></Drawer.CloseTrigger>
            </Drawer.Content></Drawer.Positioner></Portal>
        </Drawer.Root>
    );
}

export function PaymentDetail() {
    const { id } = useParams<{ id: string }>();
    const { getAccessTokenSilently } = useAuth0();
    const [editOpen, setEditOpen] = useState(false);
    const [attachmentUrl, setAttachmentUrl] = useState<string | null>(null);
    const [uploading, setUploading] = useState(false);

    const { data: paymentData, error, isLoading, isAuthenticated, mutate } =
        useAuthorizedRequest<{ payment: Payment }>(`/payments/${id}`, jsonFetcher);

    const { data: clientData } =
        useAuthorizedRequest<{ client: Client }>(
            paymentData?.payment ? `/clients/${paymentData.payment.client_id}` : null!,
            jsonFetcher
        );

    async function handleEditPayment(attrs: PaymentAttributes) {
        const token = await getAccessTokenSilently();
        await apiRequest('PUT', `/payments/${id}`, token, attrs);
        await mutate();
        globalMutate(
            (key: unknown) => typeof key === 'string' && key.includes('/reports/most-indebted'),
            undefined,
            { revalidate: true }
        );
    }

    const handleFetchAttachment = useCallback(async () => {
        const token = await getAccessTokenSilently();
        const res = await apiRequest<{ download_url: string }>('GET', `/payments/${id}/attachment`, token);
        setAttachmentUrl(res.download_url);
    }, [id, getAccessTokenSilently]);

    async function handleUploadAttachment(file: File) {
        setUploading(true);
        try {
            const token = await getAccessTokenSilently();
            const res = await apiRequest<{ upload_url: string }>('POST', `/payments/${id}/attachment/upload`, token);
            // Upload file directly to S3 via presigned URL
            const s3res = await fetch(res.upload_url, { method: 'PUT', body: file });
            if (!s3res.ok) {
                throw new Error(t('payments.upload_failed'));
            }
            await mutate();
            globalMutate(
                (key: unknown) => typeof key === 'string' && key.includes('/reports/most-indebted'),
                undefined,
                { revalidate: true }
            );
            await handleFetchAttachment();
        } catch (e) {
            throw e; // re-throw so the caller can handle it
        } finally {
            setUploading(false);
        }
    }

    async function handleDeleteAttachment() {
        if (!window.confirm(t('payments.remove_confirm'))) return;
        const token = await getAccessTokenSilently();
        await apiRequest('DELETE', `/payments/${id}/attachment`, token);
        setAttachmentUrl(null);
        await mutate();
        globalMutate(
            (key: unknown) => typeof key === 'string' && key.includes('/reports/most-indebted'),
            undefined,
            { revalidate: true }
        );
    }

    if (!isAuthenticated) return <div>{t('label.needs_auth')}</div>;
    if (error) return <div>{t('label.error')}: {(error as Error).message}</div>;

    const payment = paymentData?.payment;
    if (!payment) return <div>{t('label.not_found')}</div>;

    const client = clientData?.client;

    return (
        <VStack gap={6} align="stretch">
            <Box>
                <HStack justify="space-between" align="start">
                    <Box>
                        <Heading size="lg">{t('payment_detail.title')}</Heading>
                        <Text color="gray.500" fontSize="sm">{payment.id}</Text>
                    </Box>
                    <HStack>
                        <Button size="sm" variant="outline" onClick={() => setEditOpen(true)}>{t('payment_detail.edit')}</Button>
                    </HStack>
                </HStack>
            </Box>
            <Card.Root>
                <Card.Body>
                    <VStack align="start" gap={3}>
                        <HStack gap={2}>
                            <Text fontWeight="bold">{t('label.client')}:</Text>
                            <Link to={`/client/${payment.client_id}`} style={{ textDecoration: 'underline' }}>
                                {client?.name ?? payment.client_id}
                            </Link>
                        </HStack>
                        <HStack gap={2}><Text fontWeight="bold">{t('label.amount')}:</Text><Text>{payment.amount} ₴</Text></HStack>
                        <HStack gap={2}><Text fontWeight="bold">{t('label.date')}:</Text><Text>{formatDate(payment.date_time)}</Text></HStack>
                        <HStack gap={2}><Text fontWeight="bold">{t('label.payment_by')}:</Text><Text>{payment.payment_by}</Text></HStack>
                        {payment.comment && <HStack gap={2} align="start"><Text fontWeight="bold">{t('label.comment')}:</Text><Text>{payment.comment}</Text></HStack>}
                        <HStack gap={2}><Text fontWeight="bold">{t('label.created_at')}:</Text><Text>{formatDate(payment.created_at)}</Text></HStack>
                        <HStack gap={2}><Text fontWeight="bold">{t('label.updated_at')}:</Text><Text>{formatDate(payment.updated_at)}</Text></HStack>
                    </VStack>
                </Card.Body>
            </Card.Root>

            {/* Attachment section */}
            <Card.Root>
                <Card.Body>
                    <HStack justify="space-between" mb={3}>
                        <Heading size="sm">{t('label.attachment')}</Heading>
                        <HStack>
                            <input
                                type="file"
                                accept="image/*"
                                hidden
                                id="attachment-upload"
                                onChange={async e => {
                                    const file = e.target.files?.[0];
                                    if (file) {
                                        try {
                                            await handleUploadAttachment(file);
                                        } catch (err) {
                                            alert(err instanceof Error ? err.message : t('payments.upload_failed'));
                                        }
                                    }
                                }}
                            />
                            <Button
                                size="xs"
                                variant="outline"
                                loading={uploading}
                                onClick={() => document.getElementById('attachment-upload')?.click()}
                            >
                                {t('payments.upload_receipt')}
                            </Button>
                            {payment.attachment_key && (
                                <Button size="xs" colorScheme="red" variant="outline" onClick={handleDeleteAttachment}>
                                    {t('action.remove')}
                                </Button>
                            )}
                        </HStack>
                    </HStack>
                    {payment.attachment_key && !attachmentUrl && (
                        <Button size="xs" variant="plain" onClick={handleFetchAttachment}>{t('payments.show_attachment')}</Button>
                    )}
                    {attachmentUrl && (
                        <Box mt={2}>
                            <Image src={attachmentUrl} alt="Payment receipt" maxH="300px" borderRadius="md" />
                        </Box>
                    )}
                </Card.Body>
            </Card.Root>

            {editOpen && <EditPaymentDrawer payment={payment} open={editOpen} onClose={() => setEditOpen(false)} onSave={handleEditPayment} />}
        </VStack>
    );
}
